import React from 'react';
import { useHistory } from 'react-router-dom';
import { WeezHeader, FilterSetController, Filter, Button, ContentCard, Card, Icon, Message } from '@weezevent/nacre';
import ImportRequests from '../../../models/importRequest';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../../user/permissions';
import { ImportsTable } from './ImportsTable';

const breakpointMobile = 600;

const Imports = ({ organization, event }) => {
    const { t } = useTranslation();
    const permissions = usePermissions();
    const history = useHistory();
    const defaultFilters = { filename: '', import_status__in: new Set(['done', 'in-progress', 'not-valid']) };
    const [filters, setFilters] = React.useState(defaultFilters);
    const [selected, setSelected] = React.useState([]);
    const [importsIds, setImportsIds] = React.useState([]);
    const [order, setOrder] = React.useState('-crst');
    const [width, setWidth] = React.useState(window.innerWidth);

    const isMobileViewport = React.useMemo(() => {
        if (width <= breakpointMobile) {
            return true;
        }
        return false;
    }, [width]);

    const displayImportCTA = React.useMemo(() => {
        const capacities = ['PyvarCapacity.IMPORT_ADMIN']; // Keep as an array to allow multiple capacities check later
        return capacities.every(cap => permissions.capacities.has(cap));
    }, [permissions]);

    React.useEffect(() => {
        const handleResizeWindow = () => setWidth(window.innerWidth);
        window.addEventListener('resize', handleResizeWindow);
        return () => {
            window.removeEventListener('resize', handleResizeWindow);
        };
    }, []);

    const [imports, loadingImports, syncImports] = ImportRequests.useApiModel(
        {
            organizationId: organization.id,
            eventId: event.id,
            cache: false,
            allow_cache: false,
            query: { filename: filters['filename'], import_status__in: Array.from(filters['import_status__in'] ? filters['import_status__in'] : []) },
            onSuccess: response => {
                setImportsIds(response.map(_import => _import.id));
            }
        },
        [filters, organization, event]
    );

    const handleSearch = React.useCallback((synth, value) => {
        setFilters(currentState => {
            const query = { ...currentState };
            query['filename'] = value?.toLowerCase();
            return query;
        });
    }, []);

    const handleFilters = React.useCallback(value => {
        setFilters(currentState => {
            let query = { ...value, filename: '' };

            if (currentState['filename'] !== undefined) {
                query['filename'] = currentState['filename'];
            }
            return query;
        });
    }, []);

    const handleSelect = React.useCallback(
        id => {
            let selection = new Set(selected);
            if (selection.has(id)) {
                selection.delete(id);
            } else {
                selection.add(id);
            }
            setSelected(Array.from(selection));
        },
        [selected, setSelected]
    );

    const handleSelectAll = React.useCallback(() => {
        const allSelected = importsIds.every(id => selected.includes(id));
        setSelected(allSelected ? [] : importsIds);
    }, [selected, importsIds]);

    return (
        <>
            <WeezHeader
                rightComponent={[
                    <Button
                        disabled={isMobileViewport}
                        key={0}
                        primary
                        icon={<Icon name={'plus'} />}
                        label={t('ticket.attendees.imports.cta')}
                        onClick={() => {
                            history.push(`/ticket/O${organization.id}/E${event.id}/attendees/imports/new`);
                        }}
                    />
                ]}
                title={t('ticket.attendees.imports.title')}
            >
                {t('ticket.attendees.imports.description')}
            </WeezHeader>

            <Message
                mini
                title={t(`ticket.attendees.imports.${isMobileViewport ? 'mobile' : 'message'}.title`)}
                type="default"
                {...(!isMobileViewport &&
                    displayImportCTA && {
                        links: [
                            {
                                label: t(`ticket.attendees.imports.message.cta`),
                                onClick: () => history.push(`/ticket/O${organization.id}/E${event.id}/attendees/import`)
                            }
                        ]
                    })}
            >
                <p>{t(`ticket.attendees.imports.${isMobileViewport ? 'mobile' : 'message'}.description`)}</p>
                {!isMobileViewport && (
                    <>
                        {t(`ticket.attendees.imports.message.cases.label`)}
                        <ul>
                            <li>{t(`ticket.attendees.imports.message.cases.seated`)}</li>
                            <li>{t(`ticket.attendees.imports.message.cases.sessions`)}</li>
                            <li>{t(`ticket.attendees.imports.message.cases.custom_fields`)}</li>
                        </ul>
                    </>
                )}
            </Message>

            <Card>
                <ContentCard>
                    <FilterSetController
                        total={imports?.length}
                        isLoadingTotal={loadingImports}
                        labelTotal={t('ticket.attendees.imports.count', {
                            count: imports?.length || 0
                        })}
                        labelReset={t('ticket.common.action.reset')}
                        labelFilters={t('ticket.common.cta.filters')}
                        onChange={value => handleFilters(value)}
                        initialValues={filters}
                        defaultValues={defaultFilters}
                        onReset={() => setFilters(defaultFilters)}
                        search={{
                            onChange: (synth, value) => {
                                handleSearch(synth, value);
                            },
                            handleReset: () => {
                                handleSearch('');
                            },
                            placeholder: t('ticket.common.action.search'),
                            value: filters['filename']
                        }}
                    >
                        <Filter
                            index="1"
                            labelCTA={t('ticket.common.action.apply')}
                            labelSelectAll={t('ticket.common.action.select-all')}
                            labelName={t('ticket.common.status')}
                            name="import_status__in"
                            multiple
                            options={[
                                {
                                    key: 'imported',
                                    name: 'done',
                                    text: t('ticket.attendees.imports.status.imported')
                                },
                                {
                                    key: 'failed',
                                    name: 'not-valid',
                                    text: t('ticket.attendees.imports.status.failed')
                                },
                                {
                                    key: 'pending',
                                    name: 'valid',
                                    text: t('ticket.attendees.imports.status.pending')
                                },
                                {
                                    key: 'in-progress',
                                    name: 'in-progress',
                                    text: t('ticket.attendees.imports.status.in-progress')
                                }
                            ]}
                        />
                    </FilterSetController>
                </ContentCard>
                <ImportsTable
                    event={event}
                    order={order}
                    ids={importsIds}
                    imports={imports}
                    sync={syncImports}
                    setOrder={setOrder}
                    selected={selected}
                    loading={loadingImports}
                    handleSelect={handleSelect}
                    handleSelectAll={handleSelectAll}
                />
                {!loadingImports && !imports.length && (
                    <Card.EmptyState
                        icon={<Icon name="list" />}
                        title={t('ticket.attendees.imports.emptycard.label')}
                        labelButton={t('ticket.attendees.imports.cta')}
                        onClick={() => {
                            history.push(`/ticket/O${organization.id}/E${event.id}/attendees/imports/new`);
                        }}
                    />
                )}
            </Card>
        </>
    );
};

export default Imports;
